import { inject, Injectable, OnDestroy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { VendorNativeAlarmService } from '../../services/vendor-native-alarm.service';
import {
  Order,
  OrderStatus,
  VendorOrderStatus,
  DeliveryOrderStatus,
  VendorContextService,
  ApiOrdersService,
  ApiWebSocketService,
  VendorOrdersTabFilter,
  isOrderCancelled as checkOrderCancelled,
} from '@shared';
import {
  auditTime,
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

export const VENDOR_ORDERS_TAB_PAGE_SIZE = 50;
const TAB_PAGE_SIZE = VENDOR_ORDERS_TAB_PAGE_SIZE;

type TabKey = VendorOrdersTabFilter;

interface TabSnapshot {
  orders: Order[];
  offset: number;
  hasMore: boolean;
  loaded: boolean;
  total: number;
  /** yyyy-MM-dd range used for the current snapshot (undefined = no date filter). */
  loadedStartDate?: string;
  loadedEndDate?: string;
}

const emptyTab = (): TabSnapshot => ({
  orders: [],
  offset: 0,
  hasMore: false,
  loaded: false,
  total: 0,
});

type VendorTabCounts = {
  new: number;
  inProgress: number;
  preparing: number;
  ready: number;
  completed: number;
  cancelled: number;
};

@Injectable({ providedIn: 'root' })
export class VendorOrdersService implements OnDestroy {
  private readonly apiOrdersService = inject(ApiOrdersService);
  private readonly vendorContext = inject(VendorContextService);
  private readonly wsService = inject(ApiWebSocketService);
  private readonly nativeAlarm = inject(VendorNativeAlarmService);
  private readonly destroy$ = new Subject<void>();
  private readonly wsReload$ = new Subject<void>();
  private readonly ptrReload$ = new Subject<void>();
  /** Skip WS-driven list reload right after this vendor's own status API call (WS echoes the same change). */
  private suppressWsReloadUntil = 0;
  private reloadNewAndInProgressInFlight: Promise<void> | null = null;
  private readonly cacheTimestamp$ = new BehaviorSubject<number>(Date.now());
  /** True while the live Orders route is active (controls new/in_progress list fetches). */
  private readonly ordersTabsActive$ = new BehaviorSubject<boolean>(false);
  /** True while the Orders History route is active (controls completed/cancelled list fetches). */
  private readonly ordersHistoryActive$ = new BehaviorSubject<boolean>(false);
  private readonly tabCounts$ = new BehaviorSubject({
    new: 0,
    inProgress: 0,
    preparing: 0,
    ready: 0,
    completed: 0,
    cancelled: 0,
  });
  private autoRefreshInterval?: ReturnType<typeof setInterval>;
  /** Orders page date filter; endDate omitted when range ends today (open through now). */
  private tabDateRange: { startDate: string; endDate?: string } | null = null;
  private tabCountsRefreshInFlight: Promise<VendorTabCounts> | null = null;

  private readonly vendorId$ = toObservable(this.vendorContext.vendor).pipe(
    map((vendor) => vendor?.id ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly tab$ = {
    new: new BehaviorSubject<TabSnapshot>(emptyTab()),
    in_progress: new BehaviorSubject<TabSnapshot>(emptyTab()),
    completed: new BehaviorSubject<TabSnapshot>(emptyTab()),
    cancelled: new BehaviorSubject<TabSnapshot>(emptyTab()),
  };

  private deriveOrderStatus(
    vendorStatus: VendorOrderStatus | undefined,
    deliveryStatus: DeliveryOrderStatus | undefined,
    originalStatus?: OrderStatus,
  ): OrderStatus {
    if (originalStatus === 'cancelled') {
      return 'cancelled';
    }
    if (vendorStatus === 'cancelled') {
      return 'cancelled';
    }
    if (originalStatus === 'delivered' || deliveryStatus === 'delivered') {
      return 'delivered';
    }
    if (deliveryStatus === 'out-for-delivery' || deliveryStatus === 'picked-up') {
      return 'out-for-delivery';
    }
    if (vendorStatus === 'ready') {
      return 'out-for-delivery';
    }
    if (vendorStatus === 'preparing') {
      return 'preparing';
    }
    if (vendorStatus === 'accepted') {
      return 'accepted';
    }
    if (vendorStatus === 'new') {
      return 'pending';
    }
    if (originalStatus) {
      return originalStatus;
    }
    return 'pending';
  }

  private normalizeOrderVendorStatus(order: Order): Order {
    if (checkOrderCancelled(order)) {
      return {
        ...order,
        vendorStatus: 'cancelled',
        status: 'cancelled',
      };
    }

    let vendorStatus: VendorOrderStatus | undefined;
    if (order.vendorStatus && ['new', 'accepted', 'preparing', 'ready'].includes(order.vendorStatus)) {
      vendorStatus = order.vendorStatus;
    } else {
      if (order.status === 'pending') {
        vendorStatus = 'new';
      } else if (order.status === 'accepted') {
        vendorStatus = 'accepted';
      } else if (order.status === 'preparing') {
        vendorStatus = 'preparing';
      } else if (order.status === 'out-for-delivery' || order.status === 'picked-up') {
        if (order.deliveryStatus === 'unassigned' || order.deliveryStatus === 'assigned' || !order.deliveryStatus) {
          vendorStatus = 'ready';
        } else {
          vendorStatus = 'ready';
        }
      } else if (order.status === 'delivered' || order.status === 'cancelled') {
        vendorStatus = undefined;
      }
    }

    const derivedStatus = this.deriveOrderStatus(vendorStatus, order.deliveryStatus, order.status);

    return {
      ...order,
      vendorStatus,
      status: derivedStatus,
    };
  }

  private mapApiOrder(apiOrder: any, effectiveVendorId: string): Order {
    let perVendorStatus: string | undefined;
    if (apiOrder.vendors && effectiveVendorId) {
      const vendorSummary = apiOrder.vendors.find(
        (v: any) => v.id === effectiveVendorId || v.vendorId === effectiveVendorId,
      );
      if (vendorSummary?.status) {
        perVendorStatus = vendorSummary.status;
      }
    }

    const vendorStatusRaw = perVendorStatus || apiOrder.vendorStatus;
    const vendorStatus: VendorOrderStatus | undefined =
      vendorStatusRaw === 'new' ||
      vendorStatusRaw === 'accepted' ||
      vendorStatusRaw === 'preparing' ||
      vendorStatusRaw === 'ready' ||
      vendorStatusRaw === 'cancelled'
        ? (vendorStatusRaw as VendorOrderStatus)
        : undefined;

    const normalizedOrder: Order = {
      ...apiOrder,
      vendorStatus,
      deliveryStatus: apiOrder.deliveryStatus,
    };
    return this.normalizeOrderVendorStatus(normalizedOrder);
  }

  /** Normalize API order for vendor UI (same rules as tab lists / Active tab). */
  mapOrderForVendor(apiOrder: any, vendorId: string): Order {
    return this.mapApiOrder(apiOrder, vendorId);
  }

  private getMergedOrders(): Order[] {
    const byId = new Map<string, Order>();
    (['new', 'in_progress', 'completed', 'cancelled'] as TabKey[]).forEach((t) => {
      for (const o of this.tab$[t].value.orders) {
        byId.set(o.id, o);
      }
    });
    return Array.from(byId.values()).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }

  readonly allOrders$: Observable<Order[]> = combineLatest([
    this.tab$.new,
    this.tab$.in_progress,
    this.tab$.completed,
    this.tab$.cancelled,
  ]).pipe(
    map(() => this.getMergedOrders()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly pendingOrders$: Observable<Order[]> = this.tab$.new.pipe(
    map((s) => s.orders),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** First unaccepted order in the live queue (for notification deep links). */
  getFirstPendingOrder(): Order | undefined {
    return this.tab$.new.value.orders[0];
  }

  readonly inProgressOrders$: Observable<Order[]> = this.tab$.in_progress.pipe(
    map((s) => s.orders),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly completedOrders$: Observable<Order[]> = this.tab$.completed.pipe(
    map((s) => s.orders),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly cancelledOrders$: Observable<Order[]> = this.tab$.cancelled.pipe(
    map((s) => s.orders),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly completedTabMeta$ = this.tab$.completed.pipe(
    map((s) => ({
      hasMore: s.hasMore,
      loaded: s.loaded,
      orderCount: s.orders.length,
      total: s.total,
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly cancelledTabMeta$ = this.tab$.cancelled.pipe(
    map((s) => ({
      hasMore: s.hasMore,
      loaded: s.loaded,
      orderCount: s.orders.length,
      total: s.total,
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Lightweight tab badge counts (shell); not the paginated tab list APIs. */
  readonly vendorTabCounts$ = this.tabCounts$.asObservable();

  /** Current unanswered New-tab count (for ring gating). */
  peekNewOrderCount(): number {
    return this.tabCounts$.value.new;
  }

  /**
   * When a new-order alert arrives before tab-count refresh, seed New=1 so
   * continuous-ring loops stay allowed until the server count catches up.
   */
  seedPendingNewCountIfEmpty(): void {
    const c = this.tabCounts$.value;
    if (c.new > 0) {
      return;
    }
    this.tabCounts$.next({ ...c, new: 1 });
  }

  constructor() {
    this.vendorId$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((id) => {
        if (!id) {
          this.resetTabs();
          this.tabCounts$.next({
            new: 0,
            inProgress: 0,
            preparing: 0,
            ready: 0,
            completed: 0,
            cancelled: 0,
          });
          return;
        }
        void this.refreshTabCounts();
      });

    merge(this.wsService.onNewOrder, this.wsService.onOrderStatusUpdate.pipe(auditTime(400)))
      .pipe(takeUntil(this.destroy$))
      .subscribe((evt) => {
        if (!this.wsEventRelevant(evt)) {
          return;
        }
        if (Date.now() < this.suppressWsReloadUntil) {
          return;
        }
        this.wsReload$.next();
      });

    this.wsService.onOrderStatusUpdate
      .pipe(takeUntil(this.destroy$), auditTime(400))
      .subscribe((evt) => {
        if (!this.wsEventRelevant(evt)) {
          return;
        }
        const status = evt?.status ?? evt?.data?.status;
        if (status === 'delivered' && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('vendor:order-delivered'));
        }
      });

    merge(this.wsReload$.pipe(auditTime(600)), this.ptrReload$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.ordersTabsActive$.value) {
          void this.refreshTabCounts();
          return;
        }
        void this.requestReloadNewAndInProgress();
      });

    this.autoRefreshInterval = setInterval(() => {
      if (!this.ordersTabsActive$.value) {
        return;
      }
      const cacheAge = Date.now() - this.cacheTimestamp$.value;
      if (cacheAge > 5 * 60 * 1000) {
        const vid = this.vendorContext.vendor()?.id ?? this.vendorContext.vendorId();
        if (vid) {
          void this.requestReloadNewAndInProgress();
        }
      }
    }, 5 * 60 * 1000);
  }

  /** Call when entering the live Orders route (list loads are driven by the page per tab). */
  activateOrdersTabs(): void {
    this.ordersHistoryActive$.next(false);
    this.ordersTabsActive$.next(true);
  }

  /** Call when entering the Orders History route. */
  activateOrdersHistory(): void {
    this.ordersTabsActive$.next(false);
    this.ordersHistoryActive$.next(true);
  }

  setTabDateRange(range: { startDate: string; endDate?: string } | null): void {
    this.tabDateRange = range;
  }

  invalidateVendorTab(tab: TabKey): void {
    this.tab$[tab].next(emptyTab());
  }

  /** Call when leaving the live Orders route — stops paginated tab refetches until re-entered. */
  deactivateOrdersTabs(): void {
    this.ordersTabsActive$.next(false);
  }

  /** Call when leaving the Orders History route. */
  deactivateOrdersHistory(): void {
    this.ordersHistoryActive$.next(false);
  }

  /** Sidebar badge: aggregated new / in-progress counts (single lightweight API). */
  async refreshTabCounts(force = false): Promise<VendorTabCounts> {
    if (!force && this.tabCountsRefreshInFlight) {
      return this.tabCountsRefreshInFlight;
    }
    if (force && this.tabCountsRefreshInFlight) {
      try {
        await this.tabCountsRefreshInFlight;
      } catch {
        // ignore previous failure; fetch a fresh snapshot for sound gating
      }
    }
    this.tabCountsRefreshInFlight = this.fetchTabCountsOnce().finally(() => {
      this.tabCountsRefreshInFlight = null;
    });
    return this.tabCountsRefreshInFlight;
  }

  private async fetchTabCountsOnce(): Promise<VendorTabCounts> {
    const vendorId = this.vendorContext.vendor()?.id ?? this.vendorContext.vendorId();
    if (!vendorId) {
      const empty: VendorTabCounts = {
        new: 0,
        inProgress: 0,
        preparing: 0,
        ready: 0,
        completed: 0,
        cancelled: 0,
      };
      this.tabCounts$.next(empty);
      return empty;
    }
    try {
      const previous = { ...this.tabCounts$.value };
      const c = await firstValueFrom(this.apiOrdersService.getVendorTabCounts());
      const counts: VendorTabCounts = {
        new: c?.new ?? 0,
        inProgress: c?.inProgress ?? 0,
        preparing: c?.preparing ?? 0,
        ready: c?.ready ?? 0,
        completed: c?.completed ?? 0,
        cancelled: c?.cancelled ?? 0,
      };
      this.tabCounts$.next(counts);
      this.syncOrderListsWithCounts(previous, counts);
      return counts;
    } catch {
      return this.tabCounts$.value;
    }
  }

  /** True when a loaded tab snapshot no longer matches the server count (first page only). */
  private isTabListStale(tab: 'new' | 'in_progress', count: number): boolean {
    const snap = this.tab$[tab].value;
    if (!snap.loaded || snap.hasMore) {
      return false;
    }
    return count !== snap.orders.length;
  }

  /** Invalidate stale tab caches and refetch when counts drift from in-memory lists. */
  private syncOrderListsWithCounts(previous: VendorTabCounts, counts: VendorTabCounts): void {
    if (this.reloadNewAndInProgressInFlight) {
      return;
    }

    const newStale = counts.new !== previous.new || this.isTabListStale('new', counts.new);
    const inProgressStale =
      counts.inProgress !== previous.inProgress ||
      this.isTabListStale('in_progress', counts.inProgress);

    if (!newStale && !inProgressStale) {
      return;
    }

    if (this.ordersTabsActive$.value) {
      void this.requestReloadNewAndInProgress();
      return;
    }

    if (newStale) {
      this.invalidateVendorTab('new');
    }
    if (inProgressStale) {
      this.invalidateVendorTab('in_progress');
    }
  }

  private wsEventRelevant(data: any): boolean {
    const vid = this.vendorContext.vendor()?.id ?? this.vendorContext.vendorId();
    if (!vid) {
      return false;
    }
    if (data?.vendorId === vid) {
      return true;
    }
    const order = data?.order ?? data?.data;
    const vendors = order?.vendors;
    if (Array.isArray(vendors)) {
      return vendors.some((v: any) => v.id === vid || v.vendorId === vid);
    }
    return false;
  }

  private resetTabs(): void {
    (['new', 'in_progress', 'completed', 'cancelled'] as TabKey[]).forEach((t) =>
      this.tab$[t].next(emptyTab()),
    );
  }

  async loadVendorTab(
    tab: TabKey,
    offset: number,
    options?: { append?: boolean; forceRefresh?: boolean },
  ): Promise<void> {
    const vendorId = await firstValueFrom(
      this.vendorId$.pipe(
        filter((id): id is string => !!id),
        take(1),
      ),
    );
    await this.fetchTabInternal(
      tab,
      offset,
      vendorId,
      options?.append ?? false,
      options?.forceRefresh ?? false,
    );
    this.cacheTimestamp$.next(Date.now());
  }

  /** Load the next page and append to the in-memory tab list. Returns false if nothing was loaded. */
  async appendVendorTab(tab: TabKey): Promise<boolean> {
    const snap = this.tab$[tab].value;
    if (!snap.loaded || !snap.hasMore) {
      return false;
    }
    const previousCount = snap.orders.length;
    const vendorId = await firstValueFrom(
      this.vendorId$.pipe(
        filter((id): id is string => !!id),
        take(1),
      ),
    );
    await this.fetchTabInternal(tab, previousCount, vendorId, true);
    this.cacheTimestamp$.next(Date.now());
    return this.tab$[tab].value.orders.length > previousCount;
  }

  private mergeOrderPages(existing: Order[], incoming: Order[]): Order[] {
    if (existing.length === 0) {
      return incoming;
    }
    const seen = new Set(existing.map((o) => o.id));
    const added = incoming.filter((o) => !seen.has(o.id));
    return [...existing, ...added];
  }

  private async fetchTabInternal(
    tab: TabKey,
    offset: number,
    vendorId: string,
    append: boolean,
    forceRefresh = false,
  ): Promise<void> {
    const startDate = this.tabDateRange?.startDate;
    const endDate = this.tabDateRange?.endDate;
    const snap = this.tab$[tab].value;
    if (
      !forceRefresh &&
      !append &&
      offset === 0 &&
      snap.loaded &&
      snap.loadedStartDate === startDate &&
      snap.loadedEndDate === endDate
    ) {
      return;
    }
    try {
      const res = await firstValueFrom(
        this.apiOrdersService
          .getVendorTabPage({
            vendorTab: tab,
            limit: TAB_PAGE_SIZE,
            offset,
            vendorId,
            ...(startDate
              ? { startDate, ...(endDate ? { endDate } : {}) }
              : {}),
          })
          .pipe(
            catchError(() =>
              of({
                data: [] as Order[],
                total: 0,
                limit: TAB_PAGE_SIZE,
                offset,
                hasMore: false,
              }),
            ),
          ),
      );
      const page = res && typeof res === 'object' && 'data' in res ? res : null;
      const rows = page?.data ?? (Array.isArray(res) ? res : []);
      const mapped = rows.map((o: any) => this.mapApiOrder(o, vendorId));
      const orders = append ? this.mergeOrderPages(snap.orders, mapped) : mapped;
      const total = typeof page?.total === 'number' ? page.total : undefined;
      const hasMore =
        typeof page?.hasMore === 'boolean'
          ? page.hasMore
          : total !== undefined
            ? offset + mapped.length < total
            : mapped.length >= TAB_PAGE_SIZE;
      this.tab$[tab].next({
        orders,
        offset,
        hasMore,
        loaded: true,
        total: total ?? orders.length,
        loadedStartDate: startDate,
        loadedEndDate: endDate,
      });
    } catch (err) {
      if (append) {
        throw err;
      }
      // keep previous snapshot on initial load failure
    }
  }

  readonly tabHasMore = (tab: TabKey): boolean => this.tab$[tab].value.hasMore;
  readonly tabOffset = (tab: TabKey): number => this.tab$[tab].value.offset;

  private noteLocalOrderMutation(): void {
    this.suppressWsReloadUntil = Date.now() + 2000;
  }

  private requestReloadNewAndInProgress(): Promise<void> {
    if (!this.ordersTabsActive$.value) {
      return Promise.resolve();
    }
    if (this.reloadNewAndInProgressInFlight) {
      return this.reloadNewAndInProgressInFlight;
    }
    this.reloadNewAndInProgressInFlight = this.reloadNewAndInProgress().finally(() => {
      this.reloadNewAndInProgressInFlight = null;
    });
    return this.reloadNewAndInProgressInFlight;
  }

  private async reloadNewAndInProgress(): Promise<void> {
    if (!this.ordersTabsActive$.value) {
      return;
    }
    const vendorId = await firstValueFrom(
      this.vendorId$.pipe(
        filter((id): id is string => !!id),
        take(1),
      ),
    );
    this.tab$.new.next(emptyTab());
    this.tab$.in_progress.next(emptyTab());
    await Promise.all([
      this.fetchTabInternal('new', 0, vendorId, false),
      this.fetchTabInternal('in_progress', 0, vendorId, false),
    ]);
    this.cacheTimestamp$.next(Date.now());
    void this.refreshTabCounts();
  }

  async updateOrderStatus(orderId: string, status: OrderStatus, reason?: string): Promise<void> {
    let vendorStatus: VendorOrderStatus | undefined;
    if (status === 'pending') {
      vendorStatus = 'new';
    } else if (status === 'accepted') {
      vendorStatus = 'accepted';
    } else if (status === 'preparing') {
      vendorStatus = 'preparing';
    } else if (status === 'out-for-delivery') {
      vendorStatus = 'ready';
    }

    const updatePayload: any = {};
    if (reason) {
      updatePayload.reason = reason;
    }
    if (vendorStatus) {
      updatePayload.vendorStatus = vendorStatus;
    } else {
      updatePayload.status = status;
    }
    const vendorId = await firstValueFrom(this.vendorId$);
    if (vendorId) {
      updatePayload.vendorId = vendorId;
    }
    await this.retryWithBackoff(() =>
      firstValueFrom(this.apiOrdersService.updateStatus(orderId, updatePayload)),
    );
    this.noteLocalOrderMutation();
    if (this.ordersTabsActive$.value) {
      await this.requestReloadNewAndInProgress();
    } else {
      void this.refreshTabCounts();
    }
  }

  async acceptOrder(orderId: string): Promise<void> {
    this.optimisticallyDecrementNewTabCount();
    await this.updateOrderStatus(orderId, 'accepted');
  }

  async rejectOrder(orderId: string, reason?: string): Promise<void> {
    this.optimisticallyDecrementNewTabCount();
    const rejectionReason = reason || 'Order rejected by vendor';
    const vendorId = await firstValueFrom(this.vendorId$);
    const updatePayload: { vendorStatus: string; reason: string; vendorId?: string } = {
      vendorStatus: 'cancelled',
      reason: rejectionReason,
    };
    if (vendorId) {
      updatePayload.vendorId = vendorId;
    }
    await this.retryWithBackoff(() =>
      firstValueFrom(this.apiOrdersService.updateStatus(orderId, updatePayload)),
    );
    this.noteLocalOrderMutation();
    if (this.ordersTabsActive$.value) {
      await this.requestReloadNewAndInProgress();
    } else if (this.ordersHistoryActive$.value) {
      const vid = vendorId ?? (await firstValueFrom(this.vendorId$));
      if (vid) {
        this.tab$.cancelled.next(emptyTab());
        await this.fetchTabInternal('cancelled', 0, vid, false);
      }
    } else {
      void this.refreshTabCounts();
    }
  }

  async markPreparing(orderId: string): Promise<void> {
    await this.updateOrderStatus(orderId, 'preparing');
  }

  async markReady(orderId: string): Promise<void> {
    await this.updateOrderStatus(orderId, 'out-for-delivery');
  }

  async deleteOrder(orderId: string): Promise<void> {
    await this.rejectOrder(orderId);
  }

  async removeOrderFromView(orderId: string): Promise<void> {
    const order = this.getMergedOrders().find((o) => o.id === orderId);
    if (!order) {
      return;
    }
    const isFinalState =
      order.status === 'delivered' || order.status === 'cancelled' || checkOrderCancelled(order);
    const isReadyNotPickedUp =
      (order.status === 'out-for-delivery' || order.vendorStatus === 'ready') &&
      order.deliveryStatus !== 'picked-up' &&
      order.deliveryStatus !== 'out-for-delivery';
    if (!isFinalState && !isReadyNotPickedUp) {
      throw new Error('Cannot remove active order from view. Use reject/cancel instead.');
    }
    (['completed', 'cancelled', 'new', 'in_progress'] as TabKey[]).forEach((t) => {
      const s = this.tab$[t].value;
      if (!s.orders.some((o) => o.id === orderId)) {
        return;
      }
      this.tab$[t].next({
        ...s,
        orders: s.orders.filter((o) => o.id !== orderId),
      });
    });
  }

  private async retryWithBackoff<T>(fn: () => Promise<T>, maxRetries = 3, initialDelay = 1000): Promise<T> {
    let lastError: any;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;
        if (error?.status && error.status >= 400 && error.status < 500 && error.status !== 408) {
          throw error;
        }
        if (attempt === maxRetries) {
          throw error;
        }
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    throw lastError;
  }

  refreshOrders(): void {
    this.ptrReload$.next();
  }

  /** Awaitable reload of new + in-progress tabs when the Orders route is active. */
  reloadLiveOrderTabs(): Promise<void> {
    return this.requestReloadNewAndInProgress();
  }
  
  /** Instantly drop the new-tab badge so reminder audio/notifications stop before the API round-trip. */
  private optimisticallyDecrementNewTabCount(): void {
    const c = this.tabCounts$.value;
    if (c.new <= 0) {
      return;
    }
    const nextNew = Math.max(0, c.new - 1);
    this.tabCounts$.next({ ...c, new: nextNew });
    if (nextNew === 0) {
      this.nativeAlarm.stopAllSounds();
    } else {
      void this.nativeAlarm.setContinueAllowed(true);
    }
  }

  ngOnDestroy(): void {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
    this.ordersTabsActive$.complete();
    this.tabCounts$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCacheTimestamp(): number {
    return this.cacheTimestamp$.value;
  }

  get wsConnected$(): Observable<boolean> {
    return this.wsService.connected;
  }

  isCacheStale(): boolean {
    return Date.now() - this.cacheTimestamp$.value > 5 * 60 * 1000;
  }
}
